import { cn } from '@nt/ui/cn'
import * as React from 'react'

import { Input } from '@/components/ui/input'
import { TaxIdType } from '@/types/enums'

interface TaxIdInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string
  onChange: (value: string) => void
  taxIdType: TaxIdType | ''
}

// Format patterns for each tax ID type
const formatTaxId = (value: string, type: TaxIdType | ''): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')

  switch (type) {
    case TaxIdType.SSN:
      // SSN: XXX-XX-XXXX
      if (digits.length <= 3) return digits
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`

    case TaxIdType.EIN:
      // EIN: XX-XXXXXXX
      if (digits.length <= 2) return digits
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`

    case TaxIdType.ITIN:
      // ITIN: 9XX-XX-XXXX (must start with 9)
      if (digits.length <= 3) return digits
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`

    case TaxIdType.FTIN:
      // FTIN: Allow alphanumeric, up to 20 chars
      return value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 20)

    default:
      return value
  }
}

// Get max length based on type (formatted length)
const getMaxLength = (type: TaxIdType | ''): number => {
  switch (type) {
    case TaxIdType.SSN:
    case TaxIdType.ITIN:
      return 11 // XXX-XX-XXXX
    case TaxIdType.EIN:
      return 10 // XX-XXXXXXX
    case TaxIdType.FTIN:
      return 20
    default:
      return 20
  }
}

export function TaxIdInput({ value, onChange, taxIdType, className, ...props }: TaxIdInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // For FTIN, allow alphanumeric
    if (taxIdType === TaxIdType.FTIN) {
      onChange(formatTaxId(inputValue, taxIdType))
      return
    }

    // For US tax IDs, only allow digits and format
    const formatted = formatTaxId(inputValue, taxIdType)
    onChange(formatted)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // For US tax IDs, prevent letter input
    if (taxIdType !== TaxIdType.FTIN && taxIdType !== '') {
      // Allow: backspace, delete, tab, escape, enter, arrows
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
      if (allowedKeys.includes(e.key)) return

      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) return

      // Block non-digit characters
      if (!/^\d$/.test(e.key)) {
        e.preventDefault()
      }
    }
  }

  return (
    <Input
      {...props}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      maxLength={getMaxLength(taxIdType)}
      className={cn(className)}
      inputMode={taxIdType === TaxIdType.FTIN ? 'text' : 'numeric'}
    />
  )
}
